import { agentRunsRefetchInterval } from './agentRunsPolling';
import type { RecordedOperatorStep } from '@erp/rpc';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { orpc, rpcClient } from 'src/api/rpc/client';
import { invalidateNamespaceQueries } from 'src/api/rpc/hooks/namespaces/useNamespaces';
import type {
  AgentBundle,
  ImportAgentBundleInput,
  ImportAgentBundleResponse,
  AgentDefinitionResponse,
  AgentDefinitionSummary,
  AgentStartReadiness,
  CreateAgentDefinitionRequest,
  UpdateAgentDefinitionRequest,
  AgentTemplateResponse,
  CatalogToolMetadata,
  AgentRunCategory,
  AgentRunListFilter,
  AgentRunSummary,
  AgentDefinitionVersionEntry,
  AgentDefinitionVersionDetail,
  FinalizeAgentDefinitionVersionResponse,
  PublishAgentDefinitionVersionResponse,
  UpdateAgentDefinitionDraftRequest,
  AgentMessageResponse,
  ToolCallRunResponse,
  WorkflowPhaseStep,
  KernelTelemetrySummary,
} from '@erp/rpc';

/**
 * How often an active run's timeline is re-fetched. Exported so the trace UI can
 * tell the user the cadence it's polling at rather than hard-coding a duplicate.
 */
export const AGENT_RUN_TIMELINE_POLL_MS = 3000;

// --- Queries ---

export function useListAgentDefinitions(params?: {
  limit?: number;
  offset?: number;
  includeArchived?: boolean;
  enabled?: boolean;
}) {
  return useQuery<{ definitions: AgentDefinitionSummary[]; total: number }>({
    queryKey: [
      'agent-definitions',
      'list',
      params?.limit,
      params?.offset,
      params?.includeArchived,
    ],
    queryFn: async () => {
      return await rpcClient.agentDefinitions.list({
        limit: params?.limit,
        offset: params?.offset,
        includeArchived: params?.includeArchived,
      });
    },
    enabled: params?.enabled ?? true,
  });
}

const AGENT_DEFINITION_PAGE_SIZE = 200;

/**
 * Every active agent definition in the org, paged until `total` is reached.
 * For pickers that must be able to select — and display — any saved
 * definition; a single capped page silently hides later ones.
 */
export function useAllAgentDefinitions(params?: { enabled?: boolean }) {
  return useQuery<{ definitions: AgentDefinitionSummary[] }>({
    queryKey: ['agent-definitions', 'list', 'all'],
    queryFn: async () => {
      const definitions: AgentDefinitionSummary[] = [];
      let total = Number.POSITIVE_INFINITY;
      while (definitions.length < total) {
        const page = await rpcClient.agentDefinitions.list({
          limit: AGENT_DEFINITION_PAGE_SIZE,
          offset: definitions.length,
        });
        // A short page ends the walk even if `total` moved under us.
        if (page.definitions.length === 0) break;
        definitions.push(...page.definitions);
        total = page.total;
      }
      return { definitions };
    },
    enabled: params?.enabled ?? true,
  });
}

export function useGetAgentDefinition(id?: string) {
  return useQuery<AgentDefinitionResponse>({
    queryKey: ['agent-definitions', id],
    queryFn: async () => {
      if (!id) throw new Error('Agent definition ID is required');
      return await rpcClient.agentDefinitions.get({ agentDefinitionId: id });
    },
    enabled: !!id,
  });
}

/**
 * Whether the task-loop coordinator would start this agent now, and why.
 * Keyed under the definition so saving the definition refreshes it. Waits
 * (resources, tasks, last run) move without a definition save, so poll slowly;
 * a blocking misconfiguration only clears on save, so it isn't polled.
 */
export function useGetAgentStartReadiness(
  id: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<AgentStartReadiness>({
    queryKey: ['agent-definitions', id, 'start-readiness'],
    queryFn: async () => {
      if (!id) throw new Error('Agent definition ID is required');
      return await rpcClient.agentDefinitions.getStartReadiness({
        agentDefinitionId: id,
      });
    },
    enabled: !!id && (options?.enabled ?? true),
    refetchInterval: (query) =>
      query.state.data?.kind === 'blocking' ? false : 30_000,
  });
}

export function useListToolCatalog() {
  return useQuery<{ tools: CatalogToolMetadata[] }>({
    queryKey: ['tool-catalog'],
    queryFn: async () => {
      return await rpcClient.agentDefinitions.listToolCatalog({});
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useListAgentTemplates() {
  return useQuery<{ templates: AgentTemplateResponse[] }>({
    queryKey: ['agent-templates'],
    queryFn: async () => {
      return await rpcClient.agentDefinitions.listTemplates({});
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

export function useListAgentRuns(
  agentDefinitionId?: string,
  params?: { limit?: number; offset?: number; filter?: AgentRunListFilter },
  options?: {
    /**
     * Keep showing the previous result while a new filter or page loads,
     * instead of flashing an empty list.
     */
    keepPreviousResult?: boolean;
    /**
     * Poll for new runs (default true). Off for a list that cannot gain any,
     * such as a date range that has already ended; a listed run that is
     * still running keeps being refreshed either way.
     */
    poll?: boolean;
  },
) {
  return useQuery<{
    runs: AgentRunSummary[];
    total: number;
    /** Null from a backend that predates the field: unknown, not zero. */
    hiddenCount: number | null;
    /** Runs per category before categories are excluded; null when unknown. */
    categoryCounts: Partial<Record<AgentRunCategory, number>> | null;
    latestAttemptAt: string | null;
  }>({
    // `params` (filter included) is part of the key, so each filter caches on
    // its own and two callers asking for the same window share one request.
    queryKey: ['agent-runs', agentDefinitionId, params],
    queryFn: async () => {
      if (!agentDefinitionId)
        throw new Error('Agent definition ID is required');
      return await rpcClient.agentDefinitions.listRuns({
        agentDefinitionId,
        limit: params?.limit,
        offset: params?.offset,
        filter: params?.filter,
      });
    },
    enabled: !!agentDefinitionId,
    ...(options?.keepPreviousResult && { placeholderData: keepPreviousData }),
    // Fast poll while a run is active; slow background poll to detect new runs
    refetchInterval: (query) =>
      agentRunsRefetchInterval(
        query.state.data?.runs,
        options?.poll !== false,
        query.state.data?.categoryCounts?.running ?? null,
      ),
  });
}

export function useGetAgentRunTimeline(
  agentRunId?: string,
  options: { includeOperatorSteps?: boolean } = {},
) {
  return useQuery<{
    run: AgentRunSummary;
    operatorSteps?: RecordedOperatorStep[];
    inspectionRecording?: Awaited<
      ReturnType<typeof rpcClient.agentDefinitions.getRunTimeline>
    >['inspectionRecording'];
    messages: AgentMessageResponse[];
    toolCalls: ToolCallRunResponse[];
    workflowPhaseSteps?: WorkflowPhaseStep[];
    recordingToolCalls: ToolCallRunResponse[];
    recordingDocumentId: string | null;
    kernelTelemetryDocumentId: string | null;
    kernelTelemetrySummary: KernelTelemetrySummary | null;
    initialScreenshotDocumentId: string | null;
    initialScreenshotSignedUrl: string | null;
    // Live human-takeover state (AI-244): non-null while the operator is parked
    // awaiting a human. Rides this poll because the park may live in another
    // process (workflow worker / other replica), where the live session stream —
    // the fast path for this state — is unavailable.
    operatorTakeover: {
      state: 'paused' | 'resume_pending';
      question: string | null;
      pausedAt: string;
    } | null;
  }>({
    queryKey: [
      'agent-run-timeline',
      agentRunId,
      Boolean(options.includeOperatorSteps),
    ],
    queryFn: async () => {
      if (!agentRunId) throw new Error('Agent run ID is required');
      return await rpcClient.agentDefinitions.getRunTimeline({
        agentRunId,
        includeOperatorSteps: options.includeOperatorSteps,
      });
    },
    enabled: !!agentRunId,
    // Keep the prior timeline visible while refetching (and across the
    // running→complete handoff) so the viewer/Screenshots tab don't blink to
    // their empty state on every poll.
    placeholderData: keepPreviousData,
    // Poll while the run is still active
    refetchInterval: (query) => {
      const status = query.state.data?.run?.status;
      return status === 'running' ? AGENT_RUN_TIMELINE_POLL_MS : false;
    },
  });
}

// --- Mutations ---

export function useCreateAgentDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    AgentDefinitionResponse,
    Error,
    CreateAgentDefinitionRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.agentDefinitions.create(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agent-definitions', 'list'],
      });
    },
  });
}

export function useUpdateAgentDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    AgentDefinitionResponse,
    Error,
    UpdateAgentDefinitionRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.agentDefinitions.update(input);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['agent-definitions', data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['agent-definitions', 'list'],
      });
      invalidateNamespaceQueries(queryClient);
    },
  });
}

export function useDeleteAgentDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; clearedTaskCount?: number },
    Error,
    { agentDefinitionId: string }
  >({
    mutationFn: async (input) => {
      return await rpcClient.agentDefinitions.delete(input);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['agent-definitions', 'list'],
      });
      // Invalidate (not remove): the detail page stays mounted after an
      // archive and refetches into the archived read-only state.
      queryClient.invalidateQueries({
        queryKey: ['agent-definitions', variables.agentDefinitionId],
      });
    },
  });
}

export function useRestoreAgentDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    AgentDefinitionResponse,
    Error,
    { agentDefinitionId: string }
  >({
    mutationFn: async (input) => {
      return await rpcClient.agentDefinitions.restore(input);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['agent-definitions', data.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['agent-definitions', 'list'],
      });
    },
  });
}

export function useCloneAgentDefinition() {
  const queryClient = useQueryClient();

  return useMutation<
    AgentDefinitionResponse,
    Error,
    { agentDefinitionId: string; name?: string }
  >({
    mutationFn: async (input) => {
      return await rpcClient.agentDefinitions.clone(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['agent-definitions', 'list'],
      });
    },
  });
}

export function useStartAgentRun() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean },
    Error,
    { agentDefinitionId: string; inputData?: Record<string, unknown> }
  >({
    mutationFn: async (input) => {
      return await rpcClient.agentDefinitions.startRun(input);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['agent-runs', variables.agentDefinitionId],
      });
      // The backend creates the run record asynchronously (fire-and-forget).
      // Re-invalidate after a short delay to catch the new run.
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['agent-runs', variables.agentDefinitionId],
        });
      }, 2000);
    },
  });
}

/**
 * Durable kickoff (`agentDefinitions.launchRun`): the run row exists by the
 * time this resolves and a Temporal worker owns execution, so one
 * invalidation is enough — no delayed re-poll like `useStartAgentRun` needs.
 * Works for orchestrated agents too (their start conditions still gate the
 * work). Used by agent pages; the legacy Start button stays on `startRun`.
 */
export function useLaunchAgentRun() {
  const queryClient = useQueryClient();

  return useMutation<
    { agentRunId: string; temporalWorkflowId: string },
    Error,
    { agentDefinitionId: string; inputData?: Record<string, unknown> }
  >({
    mutationFn: async (input) => {
      return await rpcClient.agentDefinitions.launchRun(input);
    },
    // Returned (not fire-and-forget) on purpose: the mutation stays `isPending`
    // until the runs list has refetched and shows the new run, so a Run button
    // disabled on `isPending` cannot be clicked again in the gap where the
    // stale list still says nothing is running — which would launch twice.
    onSuccess: (_data, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['agent-runs', variables.agentDefinitionId],
        }),
        queryClient.invalidateQueries({
          queryKey: orpc.agentPages.statuses.key(),
        }),
      ]),
  });
}

export function useCancelAgentRun() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { agentRunId: string }>({
    mutationFn: async (input) => {
      return await rpcClient.agentDefinitions.cancelRun(input);
    },
    onSuccess: (_data, variables) => {
      // Invalidate the run timeline and the runs list so UI updates immediately
      queryClient.invalidateQueries({
        queryKey: ['agent-run-timeline', variables.agentRunId],
      });
      queryClient.invalidateQueries({ queryKey: ['agent-runs'] });
      void queryClient.invalidateQueries({
        queryKey: orpc.agentPages.statuses.key(),
      });
    },
  });
}

/**
 * Ask a running task loop to stop after its current task (`stop: true`), or
 * withdraw that request (`stop: false`).
 */
export function useSetStopAfterCurrentTask() {
  const queryClient = useQueryClient();

  return useMutation<
    { ok: boolean },
    Error,
    { agentRunId: string; stop: boolean }
  >({
    mutationFn: (input) =>
      rpcClient.agentDefinitions.setStopAfterCurrentTask(input),
    onSuccess: () => {
      // The pending request rides on the run summary, so refresh the runs list.
      void queryClient.invalidateQueries({ queryKey: ['agent-runs'] });
      void queryClient.invalidateQueries({
        queryKey: orpc.agentPages.statuses.key(),
      });
    },
  });
}

// --- Human takeover of a live operator run (agent-monitor, AI-244) ---
// Each mutation invalidates the run timeline: for a run draining in another process the
// paused/resumed state is only visible via the timeline's `operatorTakeover` field, so a
// successful pause/instruct/resume should refresh it immediately instead of waiting out
// the poll interval. (The live session stream still updates same-process viewers faster.)

function invalidateRunTimeline(
  queryClient: ReturnType<typeof useQueryClient>,
  agentRunId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: ['agent-run-timeline', agentRunId],
  });
}

/** Ask a running operator run to pause at its next safe step. */
export function usePauseOperatorRun() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, { agentRunId: string }>({
    mutationFn: (input) => rpcClient.agentDefinitions.pauseOperatorRun(input),
    onSuccess: (_data, variables) =>
      invalidateRunTimeline(queryClient, variables.agentRunId),
  });
}

/** Send a typed instruction to a paused operator run (it resumes with the instruction). */
export function useInstructOperatorRun() {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: boolean },
    Error,
    { agentRunId: string; instruction: string }
  >({
    mutationFn: (input) =>
      rpcClient.agentDefinitions.instructOperatorRun(input),
    onSuccess: (_data, variables) =>
      invalidateRunTimeline(queryClient, variables.agentRunId),
  });
}

/** Hand control back after driving the desktop during a pause (the operator re-observes). */
export function useResumeOperatorRun() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, { agentRunId: string }>({
    mutationFn: (input) => rpcClient.agentDefinitions.resumeOperatorRun(input),
    onSuccess: (_data, variables) =>
      invalidateRunTimeline(queryClient, variables.agentRunId),
  });
}

// --- Version history (AI-458) ---

/** A definition's version history, newest first. `changelog` arrives display-ready. */
export function useAgentDefinitionVersions(id: string | undefined) {
  return useQuery<{ versions: AgentDefinitionVersionEntry[] }>({
    queryKey: ['agent-definitions', id, 'versions'],
    queryFn: async () => {
      if (!id) throw new Error('Agent definition ID is required');
      return await rpcClient.agentDefinitions.listVersions({
        agentDefinitionId: id,
      });
    },
    enabled: !!id,
  });
}

/** One saved version, including the prompt, model, config, fields, and tools. */
export function useAgentDefinitionVersion(
  agentDefinitionId: string | undefined,
  versionId: string | undefined,
) {
  return useQuery<AgentDefinitionVersionDetail>({
    queryKey: ['agent-definitions', agentDefinitionId, 'version', versionId],
    queryFn: async () => {
      if (!agentDefinitionId || !versionId) {
        throw new Error('Agent definition version is required');
      }
      return await rpcClient.agentDefinitions.getVersion({
        agentDefinitionId,
        versionId,
      });
    },
    enabled: Boolean(agentDefinitionId && versionId),
  });
}

export function useLoadAgentDefinitionVersion() {
  return useMutation<
    AgentDefinitionVersionDetail,
    Error,
    { agentDefinitionId: string; versionId: string }
  >({
    mutationFn: (input) => rpcClient.agentDefinitions.getVersion(input),
  });
}

function invalidateAgentVersions(
  queryClient: ReturnType<typeof useQueryClient>,
  agentDefinitionId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: ['agent-definitions', agentDefinitionId],
  });
  void queryClient.invalidateQueries({
    queryKey: ['agent-definitions', 'list'],
  });
}

/**
 * Cache write shared by finalize and publish: both can move the published pin, so a successful
 * response carries the definition the editor should now show, and the history is stale either way.
 * A stale-base refusal (`ok: false`) changed nothing, so it must not touch the cache.
 */
function cacheVersionMove(
  queryClient: ReturnType<typeof useQueryClient>,
  agentDefinitionId: string,
  response:
    | FinalizeAgentDefinitionVersionResponse
    | PublishAgentDefinitionVersionResponse,
) {
  if (!response.ok) return;
  queryClient.setQueryData(
    ['agent-definitions', agentDefinitionId],
    response.definition,
  );
  invalidateAgentVersions(queryClient, agentDefinitionId);
}

export function useCreateAgentDefinitionDraft() {
  const queryClient = useQueryClient();
  return useMutation<
    { version: AgentDefinitionVersionDetail; resumed: boolean },
    Error,
    { agentDefinitionId: string; sourceVersionId?: string }
  >({
    mutationFn: (input) => rpcClient.agentDefinitions.createDraft(input),
    onSuccess: (_data, variables) => {
      invalidateAgentVersions(queryClient, variables.agentDefinitionId);
    },
  });
}

export function useUpdateAgentDefinitionDraft() {
  const queryClient = useQueryClient();
  return useMutation<
    AgentDefinitionVersionDetail,
    Error,
    UpdateAgentDefinitionDraftRequest
  >({
    mutationFn: (input) => rpcClient.agentDefinitions.updateDraft(input),
    onSuccess: (_data, variables) => {
      invalidateAgentVersions(queryClient, variables.agentDefinitionId);
    },
  });
}

export function useFinalizeAgentDefinitionVersion() {
  const queryClient = useQueryClient();
  return useMutation<
    FinalizeAgentDefinitionVersionResponse,
    Error,
    {
      agentDefinitionId: string;
      versionId: string;
      publish?: boolean;
      acknowledgeStaleBase?: boolean;
    }
  >({
    mutationFn: (input) => rpcClient.agentDefinitions.finalizeVersion(input),
    onSuccess: (response, variables) =>
      cacheVersionMove(queryClient, variables.agentDefinitionId, response),
  });
}

export function useDeleteAgentDefinitionDraft() {
  const queryClient = useQueryClient();
  return useMutation<
    { success: true },
    Error,
    { agentDefinitionId: string; versionId: string }
  >({
    mutationFn: (input) => rpcClient.agentDefinitions.deleteDraft(input),
    onSuccess: (_data, variables) => {
      invalidateAgentVersions(queryClient, variables.agentDefinitionId);
    },
  });
}

/**
 * Restore an older version. This changes what the agent runs next, so it invalidates the
 * definition itself as well as the history — the editor is showing the payload that just moved.
 */
export function usePublishAgentDefinitionVersion() {
  const queryClient = useQueryClient();
  return useMutation<
    PublishAgentDefinitionVersionResponse,
    Error,
    {
      agentDefinitionId: string;
      versionId: string;
      acknowledgeStaleBase?: boolean;
    }
  >({
    mutationFn: (input) => rpcClient.agentDefinitions.publishVersion(input),
    onSuccess: (response, variables) =>
      cacheVersionMove(queryClient, variables.agentDefinitionId, response),
  });
}

/** Re-derive one version's changelog (mechanical, then awaited AI prose). */
export function useRegenerateAgentDefinitionChangelog() {
  const queryClient = useQueryClient();
  return useMutation<
    { changelog: string | null },
    Error,
    { agentDefinitionId: string; versionId: string }
  >({
    mutationFn: (input) =>
      rpcClient.agentDefinitions.regenerateChangelog(input),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          'agent-definitions',
          variables.agentDefinitionId,
          'versions',
        ],
      });
    },
  });
}

// --- Export / import (agent + its procedures as one JSON bundle) ---

/** Fetch a version's portable bundle on demand (Export menu), not as a cached query. */
export function useExportAgentBundle() {
  return useMutation<
    AgentBundle,
    Error,
    { agentDefinitionId: string; versionId?: string }
  >({
    mutationFn: (input) => rpcClient.agentDefinitions.exportBundle(input),
  });
}

export function useImportAgentBundle() {
  const queryClient = useQueryClient();
  return useMutation<ImportAgentBundleResponse, Error, ImportAgentBundleInput>({
    mutationFn: (input) => rpcClient.agentDefinitions.importBundle(input),
    onSuccess: (data) => {
      invalidateAgentVersions(queryClient, data.definition.id);
      // The import may have created or versioned procedures.
      void queryClient.invalidateQueries({ queryKey: orpc.agent.key() });
    },
  });
}
